import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  Row,
  Col,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import FlatPickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { toast } from "react-toastify";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import { allowOnlyNumber } from "helpers/GeneralHelper";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { updateAIScheduleMatrix, addAIScheduleMatrix } from "model/AIScheduleMatrixModel";
import { submitAISmsTemplateFilterData } from "model/AISmsTemplateModel";

const CLIENT_LABEL_FIELD = getClientAsyncFilterLabelFieldKey();

export const AI_SCHEDULE_ACTION_TYPE = {
  CALL: 1,
  SMS: 2,
};

const ACTION_TYPE_OPTIONS = [
  { value: AI_SCHEDULE_ACTION_TYPE.CALL, label: "Call" },
  { value: AI_SCHEDULE_ACTION_TYPE.SMS, label: "SMS" },
];

const TIME_PICKER_OPTIONS = {
  enableTime: true,
  noCalendar: true,
  enableSeconds: true,
  dateFormat: "H:i:S",
  time_24hr: true,
  defaultHour: 0,
  defaultMinute: 0,
  defaultSeconds: 0,
};

const isBlankClientId = (value) => {
  if (value === null || value === undefined || value === "") return true;
  return String(value).trim() === "-1";
};

/** Normalize API time (`00:03:00`) for FlatPickr. */
const normalizeTimeValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return raw;
  const hours = String(Math.min(23, Number(match[1]))).padStart(2, "0");
  const minutes = String(Math.min(59, Number(match[2]))).padStart(2, "0");
  const seconds = String(Math.min(59, Number(match[3] ?? 0))).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const emptyForm = {
  stageId: "",
  attemptNo: "",
  dayNo: "",
  weekDayNo: "",
  slot: "",
  time: "",
  clientId: "",
  [CLIENT_LABEL_FIELD]: "",
  actionTypeId: "",
  aiSmsTemplateId: "",
  aiSmsTemplateName: "",
};

const toNumberOrZero = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pickRowValue = (row = {}, keys = []) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return "";
};

const mapSmsTemplateOptions = (list = []) =>
  (Array.isArray(list) ? list : [])
    .map((item) => {
      const value = item?.id ?? item?.Id;
      if (value == null || value === "") return null;
      const label =
        String(item?.note ?? "").trim() ||
        String(item?.name ?? item?.Name ?? "").trim() ||
        String(value);
      return { value, label };
    })
    .filter(Boolean);

const AIScheduleMatrixAddEditModal = ({
  isOpen,
  onClose,
  itemId,
  editableRow,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [smsTemplateOptions, setSmsTemplateOptions] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const isSmsAction = useMemo(
    () => Number(formData?.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS,
    [formData?.actionTypeId]
  );

  const loadSmsTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await submitAISmsTemplateFilterData({});
      const responseData = response?.data;
      const listData = Array.isArray(responseData)
        ? responseData
        : responseData?.records ?? [];
      setSmsTemplateOptions(mapSmsTemplateOptions(listData));
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const row = editableRow && typeof editableRow === "object" ? editableRow : {};
    const rawClientId = pickRowValue(row, ["clientId"]);
    const clientId = isBlankClientId(rawClientId) ? "" : String(rawClientId);
    const clientLabel = isBlankClientId(rawClientId)
      ? ""
      : String(
          pickRowValue(row, [
            CLIENT_LABEL_FIELD,
            "clientName",
            "clientTitle",
            "client",
            "ClientName",
          ]) ?? ""
        );
    const aiSmsTemplateId = pickRowValue(row, ["aiSmsTemplateId"]) ?? "";
    const aiSmsTemplateName = String(
      pickRowValue(row, ["aiSmsTemplateName", "aiSmsTemplateNote", "note"]) ?? ""
    );
    setFormData({
      stageId:
        row?.stageId === 0 || row?.stageId
          ? String(row.stageId)
          : String(pickRowValue(row, ["stageId"]) ?? ""),
      attemptNo:
        row?.attemptNo === 0 || row?.attemptNo
          ? String(row.attemptNo)
          : String(pickRowValue(row, ["attemptNo"]) ?? ""),
      dayNo:
        row?.dayNo === 0 || row?.dayNo
          ? String(row.dayNo)
          : "",
      weekDayNo:
        row?.weekDayNo === 0 || row?.weekDayNo
          ? String(row.weekDayNo)
          : "",
      slot: String(pickRowValue(row, ["slot"]) ?? ""),
      time: normalizeTimeValue(pickRowValue(row, ["time"])),
      clientId,
      [CLIENT_LABEL_FIELD]: clientLabel,
      actionTypeId: pickRowValue(row, ["actionTypeId"]) ?? "",
      aiSmsTemplateId,
      aiSmsTemplateName,
    });
    setErrors({});
    loadSmsTemplates();
  }, [isOpen, editableRow, itemId]);

  const closeModal = () => {
    setErrors({});
    onClose?.();
  };

  const handleNumberChange = (field, rawValue) => {
    const digitsOnly = String(allowOnlyNumber(rawValue ?? "")).replace(/\./g, "");
    setFormData((prev) => ({ ...prev, [field]: digitsOnly }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleTextChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value ?? "" }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleTimeChange = (_selectedDates, dateStr) => {
    handleTextChange("time", normalizeTimeValue(dateStr));
  };

  const handleClientChange = (selectedOption) => {
    setFormData((prev) =>
      applyClientAsyncFilterOptionToFormState(prev, selectedOption)
    );
    if (errors?.clientId) {
      setErrors((prev) => ({ ...prev, clientId: "" }));
    }
  };

  const handleActionTypeChange = (selectedOption) => {
    const nextValue = selectedOption?.value ?? "";
    const keepSms = Number(nextValue) === AI_SCHEDULE_ACTION_TYPE.SMS;
    setFormData((prev) => ({
      ...prev,
      actionTypeId: nextValue,
      aiSmsTemplateId: keepSms ? prev.aiSmsTemplateId : "",
      aiSmsTemplateName: keepSms ? prev.aiSmsTemplateName : "",
    }));
    setErrors((prev) => ({
      ...prev,
      actionTypeId: "",
      aiSmsTemplateId: "",
    }));
  };

  const handleSmsTemplateChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      aiSmsTemplateId: selectedOption?.value ?? "",
      aiSmsTemplateName: selectedOption?.label ?? "",
    }));
    if (errors?.aiSmsTemplateId) {
      setErrors((prev) => ({ ...prev, aiSmsTemplateId: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    ["stageId", "attemptNo"].forEach((field) => {
      if (formData?.[field] === "" || formData?.[field] == null) {
        nextErrors[field] = "Required";
      }
    });
    if (formData?.actionTypeId === "" || formData?.actionTypeId == null) {
      nextErrors.actionTypeId = "Action type is required";
    }
    if (
      Number(formData?.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS &&
      (formData?.aiSmsTemplateId === "" || formData?.aiSmsTemplateId == null)
    ) {
      nextErrors.aiSmsTemplateId = "SMS template is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;

    const payload = {
      stageId: toNumberOrZero(formData.stageId),
      attemptNo: toNumberOrZero(formData.attemptNo),
      dayNo: toOptionalNumber(formData.dayNo),
      weekDayNo: toOptionalNumber(formData.weekDayNo),
      slot: String(formData.slot ?? ""),
      time: normalizeTimeValue(formData.time) || "00:00:00",
      clientId: isBlankClientId(formData.clientId)
        ? "-1"
        : String(formData.clientId ?? ""),
      actionTypeId: toNumberOrZero(formData.actionTypeId),
      aiSmsTemplateId:
        Number(formData.actionTypeId) === AI_SCHEDULE_ACTION_TYPE.SMS
          ? formData.aiSmsTemplateId || null
          : null,
    };

    setSaving(true);
    try {
      const response = itemId
        ? await updateAIScheduleMatrix(itemId, payload)
        : await addAIScheduleMatrix(payload);
      if (response?.success === false) {
        toast.error(
          response?.message ||
            (itemId ? "Unable to update" : "Unable to create")
        );
        return;
      }
      toast.success(
        response?.message ||
          (itemId ? "Updated successfully" : "Created successfully")
      );
      closeModal();
      onSuccess?.();
    } finally {
      setSaving(false);
    }
  };

  const actionTypeValue =
    ACTION_TYPE_OPTIONS.find(
      (opt) => String(opt.value) === String(formData?.actionTypeId ?? "")
    ) || null;

  const smsTemplateValue = useMemo(() => {
    const matched = smsTemplateOptions.find(
      (opt) => String(opt.value) === String(formData?.aiSmsTemplateId ?? "")
    );
    if (matched) return matched;
    if (formData?.aiSmsTemplateId && formData?.aiSmsTemplateName) {
      return {
        value: formData.aiSmsTemplateId,
        label: formData.aiSmsTemplateName,
      };
    }
    return null;
  }, [
    smsTemplateOptions,
    formData?.aiSmsTemplateId,
    formData?.aiSmsTemplateName,
  ]);

  const smsSelectOptions = useMemo(() => {
    if (
      !formData?.aiSmsTemplateId ||
      !formData?.aiSmsTemplateName ||
      smsTemplateOptions.some(
        (opt) => String(opt.value) === String(formData.aiSmsTemplateId)
      )
    ) {
      return smsTemplateOptions;
    }
    return [
      {
        value: formData.aiSmsTemplateId,
        label: formData.aiSmsTemplateName,
      },
      ...smsTemplateOptions,
    ];
  }, [
    smsTemplateOptions,
    formData?.aiSmsTemplateId,
    formData?.aiSmsTemplateName,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      size="lg"
      className="modal-full-height"
      centered
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-uppercase">
            {itemId ? "Edit" : "Create"} AI Schedule Matrix
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Stage Id <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.stageId}
                  invalid={!!errors.stageId}
                  onChange={(e) => handleNumberChange("stageId", e.target.value)}
                />
                {errors.stageId ? (
                  <FormFeedback className="d-block">{errors.stageId}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Attempt No <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.attemptNo}
                  invalid={!!errors.attemptNo}
                  onChange={(e) => handleNumberChange("attemptNo", e.target.value)}
                />
                {errors.attemptNo ? (
                  <FormFeedback className="d-block">{errors.attemptNo}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Day No</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.dayNo}
                  onChange={(e) => handleNumberChange("dayNo", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Week Day No</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.weekDayNo}
                  onChange={(e) => handleNumberChange("weekDayNo", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Slot</Label>
                <Input
                  type="text"
                  value={formData.slot}
                  onChange={(e) => handleTextChange("slot", e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Time</Label>
                <FlatPickr
                  className="form-control"
                  placeholder="00:00:00"
                  value={formData.time || ""}
                  options={TIME_PICKER_OPTIONS}
                  onChange={handleTimeChange}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Client</Label>
                <AsyncClientSelect
                  key={`ai-schedule-matrix-client-${formData.clientId}-${isOpen}`}
                  pageName="AIScheduleMatrix"
                  value={String(formData.clientId || "").trim()}
                  initialOption={buildClientAsyncFilterInitialOption(
                    formData.clientId,
                    formData[CLIENT_LABEL_FIELD]
                  )}
                  onChange={handleClientChange}
                  placeholder="Select Client"
                  isClearable
                  isDisabled
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>
                  Action Type <span className="text-danger">*</span>
                </Label>
                <Select
                  options={ACTION_TYPE_OPTIONS}
                  className="select2-selection"
                  placeholder="Select action type"
                  value={actionTypeValue}
                  onChange={handleActionTypeChange}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={selectMenuStyles}
                />
                {errors.actionTypeId ? (
                  <FormFeedback className="d-block">{errors.actionTypeId}</FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            {isSmsAction ? (
              <Col md={6}>
                <FormGroup>
                  <Label>
                    Choose SMS Template <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={smsSelectOptions}
                    className="select2-selection"
                    placeholder={
                      loadingTemplates
                        ? "Loading templates..."
                        : "Choose SMS template"
                    }
                    isLoading={loadingTemplates}
                    value={smsTemplateValue}
                    onChange={handleSmsTemplateChange}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    styles={selectMenuStyles}
                  />
                  {errors.aiSmsTemplateId ? (
                    <FormFeedback className="d-block">
                      {errors.aiSmsTemplateId}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
              </Col>
            ) : null}
          </Row>

          <div className="text-end mt-3">
            <Button color="light" className="me-2" type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default AIScheduleMatrixAddEditModal;
